import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { storage } from '../services/storage';
import { useAuth } from '../hooks/useAuth';
import { XMarkIcon, ArrowDownTrayIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';
import { useContentProtection, logScreenshotAttempt } from '../hooks/useContentProtection';
import '../styles/content-protection.css';

const PDFViewerPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { profile } = useAuth();
  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const filePath = searchParams.get('path');
  const fileName = searchParams.get('name');
  const allowDownload = searchParams.get('download') === 'true';

  // Initialize content protection
  const watermarkText = profile?.full_name || profile?.id?.slice(0, 8) || 'YOU ARE NOT AUTHORIZED TO USE THIS REPORT!';
  useContentProtection({
    watermarkText,
    detectScreenshot: true,
    disableRightClick: true,
    onScreenshotDetected: () => {
      if (profile?.id) {
        logScreenshotAttempt(profile.id, `pdf-viewer-${fileName}`);
      }
    }
  });

  useEffect(() => {
    const loadPDF = async () => {
      if (!filePath) {
        setError('No file path provided');
        setLoading(false);
        return;
      }

      try {
        const { data, error } = await storage.getSignedUrl(filePath);
        
        if (error || !data) {
          setError('Failed to load document');
          return;
        }

        setSignedUrl(data.signedUrl);
      } catch (err) {
        setError('An error occurred loading the document');
      } finally {
        setLoading(false);
      }
    };

    loadPDF();
  }, [filePath]);

  // Check if user can download
  const hasSubscription = profile?.subscription_tier;
  const canDownload = hasSubscription && allowDownload;

  const handleDownload = async () => {
    if (!canDownload || !filePath) return;

    try {
      const { data, error } = await storage.downloadReport(filePath);
      
      if (error || !data) {
        alert('Failed to download report');
        return;
      }

      const url = window.URL.createObjectURL(data);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName || 'report.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Download failed');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-white mx-auto"></div>
          <p className="mt-4 text-white">Loading document...</p>
        </div>
      </div>
    );
  }

  if (error || !signedUrl) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <XMarkIcon className="h-16 w-16 text-red-500 mx-auto" />
          <p className="mt-4 text-white text-xl">{error || 'Failed to load document'}</p>
          <button
            onClick={() => navigate(-1)}
            className="mt-6 px-6 py-2 bg-white text-gray-900 rounded-lg hover:bg-gray-100"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-gray-900">
      {/* Header */}
      <div className="bg-gray-800 border-b border-gray-700 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
            title="Go back"
          >
            <ArrowLeftIcon className="h-5 w-5 text-white" />
          </button>
          <h1 className="text-white font-medium truncate max-w-md">
            {fileName || 'Document Viewer'}
          </h1>
        </div>
        
        <div className="flex items-center gap-2">
          {!hasSubscription && (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-yellow-500/20 border border-yellow-500/40 rounded-lg">
              <svg className="h-4 w-4 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
              </svg>
              <span className="text-xs text-yellow-200">View Only - Subscribe to Download</span>
            </div>
          )}
          
          {canDownload ? (
            <button
              onClick={handleDownload}
              className="flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors"
            >
              <ArrowDownTrayIcon className="h-4 w-4" />
              Download
            </button>
          ) : hasSubscription ? (
            <button
              disabled
              className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-gray-400 rounded-lg cursor-not-allowed"
              title="Download not available for this document"
            >
              <ArrowDownTrayIcon className="h-4 w-4" />
              Download
            </button>
          ) : (
            <button
              onClick={() => navigate('/subscription')}
              className="flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors"
            >
              <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
              </svg>
              Subscribe to Download
            </button>
          )}
        </div>
      </div>

      {/* PDF Viewer */}
      <div className="flex-1 relative bg-gray-800 protected-content">
        {/* Watermark overlay - for iframe, covers entire viewport */}
        <div className="watermark-overlay" style={{ zIndex: 999 }}>
          {[...Array(8)].map((_, i) => (
            <div 
              key={i} 
              className="watermark-text" 
              style={{ 
                color: 'rgba(255, 255, 255, 0.2)',
                fontSize: '36px',
                fontWeight: 900
              }}
            >
              {watermarkText}
            </div>
          ))}
        </div>
        
        <iframe
          src={`${signedUrl}#toolbar=0&navpanes=0`}
          className="w-full h-full border-0"
          title={fileName || 'PDF Viewer'}
          style={{
            backgroundColor: '#1f2937'
          }}
        />
        
        {/* Overlay to prevent right-click on iframe (partial protection) */}
        {!canDownload && (
          <div
            className="absolute inset-0 pointer-events-none"
            style={{ zIndex: 998 }}
            onContextMenu={(e) => e.preventDefault()}
          />
        )}
      </div>

      {/* Footer */}
      {!hasSubscription && (
        <div className="bg-gray-800 border-t border-gray-700 px-4 py-3">
          <div className="flex items-center justify-between max-w-6xl mx-auto">
            <p className="text-sm text-gray-400">
              Viewing in preview mode. Subscribe for full access and download capabilities.
            </p>
            <button
              onClick={() => navigate('/subscription')}
              className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white text-sm rounded-lg transition-colors"
            >
              View Plans
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PDFViewerPage;
